// pages/admin/ViewingRequestsPage.tsx
/**
 * @description Viewing requests management page
 */
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Calendar,
  Clock,
  ChevronLeft,
  Check,
  X,
  User,
  Mail,
  Phone,
  MessageSquare,
  Filter
} from 'lucide-react';
import { AdminLayout } from '../../components/layout';
import { Button, Badge, Modal } from '../../components/ui';
import { formatDate } from '../../lib/utils';
import { useViewingRequests, updateViewingStatus } from '../../hooks/useViewingRequests';
import { MOCK_BUILDING, MOCK_UNITS } from '../../lib/mockData';
import type { ViewingRequest, ViewingStatus } from '../../types/database';

export function ViewingRequestsPage() {
  const { slug } = useParams<{ slug: string }>();
  const [selectedRequest, setSelectedRequest] = useState<ViewingRequest | null>(null);
  const [statusFilter, setStatusFilter] = useState<ViewingStatus | 'all'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // TODO: Replace with actual building lookup
  const building = MOCK_BUILDING;

  const { requests, loading, refetch } = useViewingRequests({
    buildingId: building.id,
    status: statusFilter === 'all' ? undefined : statusFilter
  });

  const handleStatusUpdate = async (requestId: string, status: ViewingStatus) => {
    const result = await updateViewingStatus(requestId, status);
    if (result.success) {
      refetch();
      setIsModalOpen(false);
      setSelectedRequest(null);
    }
  };

  const openDetails = (request: ViewingRequest) => {
    setSelectedRequest(request);
    setIsModalOpen(true);
  };

  const getStatusBadge = (status: ViewingStatus) => {
    switch (status) {
      case 'pending':
        return <Badge variant="warning">Pending</Badge>;
      case 'confirmed':
        return <Badge variant="success">Confirmed</Badge>;
      case 'completed':
        return <Badge variant="neutral">Completed</Badge>;
      case 'cancelled':
        return <Badge variant="error">Cancelled</Badge>;
      case 'no_show':
        return <Badge variant="error">No Show</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getUnitInfo = (unitId: string | null) => {
    if (!unitId) return 'No specific unit';
    const unit = MOCK_UNITS.find(u => u.id === unitId);
    return unit ? `Unit ${unit.unit_number} - ${unit.unit_type?.name}` : 'Unknown Unit';
  };

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            to={`/admin/buildings/${slug}`}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ChevronLeft size={16} className="mr-1" />
            Back to Building
          </Link>
        </div>

        {/* Page Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Viewing Requests
            </h1>
            <p className="text-gray-500 mt-1">{building.name}</p>
          </div>
        </div>

        {/* Filter Row */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-400" />
            <span className="text-sm text-gray-600">Status:</span>
          </div>
          <div className="flex gap-2">
            {(['all', 'pending', 'confirmed', 'completed', 'cancelled'] as const).map(
              (status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    statusFilter === status
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              )
            )}
          </div>
        </div>

        {/* Requests Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : requests.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No viewing requests found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {requests.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar size={14} className="text-gray-400" />
                          <span className="text-gray-900">
                            {formatDate(request.requested_date)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm mt-1">
                          <Clock size={14} className="text-gray-400" />
                          <span className="text-gray-600">
                            {request.requested_time}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900">{request.name}</p>
                        <p className="text-sm text-gray-500">{request.email}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {getUnitInfo(request.unit_id)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(request.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetails(request)}
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Request Details Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRequest(null);
        }}
        title="Viewing Request Details"
        footer={
          selectedRequest?.status === 'pending' ? (
            <div className="flex justify-between w-full">
              <Button
                variant="secondary"
                onClick={() =>
                  selectedRequest &&
                  handleStatusUpdate(selectedRequest.id, 'cancelled')
                }
              >
                <X size={16} className="mr-2" />
                Decline
              </Button>
              <Button
                onClick={() =>
                  selectedRequest &&
                  handleStatusUpdate(selectedRequest.id, 'confirmed')
                }
              >
                <Check size={16} className="mr-2" />
                Confirm
              </Button>
            </div>
          ) : (
            <div className="flex justify-end">
              <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
                Close
              </Button>
            </div>
          )
        }
      >
        {selectedRequest && (
          <div className="space-y-4">
            {/* Status */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Status</span>
              {getStatusBadge(selectedRequest.status)}
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                  <Calendar size={14} />
                  Date
                </div>
                <p className="font-medium text-gray-900">
                  {formatDate(selectedRequest.requested_date)}
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                  <Clock size={14} />
                  Time
                </div>
                <p className="font-medium text-gray-900">
                  {selectedRequest.requested_time}
                </p>
              </div>
            </div>

            {/* Contact Info */}
            <div className="border-t border-gray-100 pt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Contact Information
              </h4>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <User size={16} className="text-gray-400" />
                  <span className="text-gray-900">{selectedRequest.name}</span>
                </div>
                <a
                  href={`mailto:${selectedRequest.email}`}
                  className="flex items-center gap-3 text-primary hover:underline"
                >
                  <Mail size={16} />
                  {selectedRequest.email}
                </a>
                {selectedRequest.phone && (
                  <a
                    href={`tel:${selectedRequest.phone}`}
                    className="flex items-center gap-3 text-primary hover:underline"
                  >
                    <Phone size={16} />
                    {selectedRequest.phone}
                  </a>
                )}
              </div>
            </div>

            {/* Unit Info */}
            <div className="border-t border-gray-100 pt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Unit</h4>
              <p className="text-gray-600">
                {getUnitInfo(selectedRequest.unit_id)}
              </p>
            </div>

            {/* Notes */}
            {selectedRequest.notes && (
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-2">
                  <MessageSquare size={14} />
                  Notes
                </div>
                <p className="text-gray-600 text-sm bg-gray-50 p-3 rounded-lg">
                  {selectedRequest.notes}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
}
